<template>
  <div class="border-t border-gray-800 py-4">
    <div class="w-full">
      <!-- Row 1: Event Logo, Teams, and Scores -->
      <div class="flex items-stretch gap-1 mb-3">
        <!-- Event Logo Box -->
        <div class="w-[52px] h-[52px] bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
          <img
            v-if="props.series.event_logo_url"
            :src="props.series.event_logo_url"
            :alt="props.series.event"
            class="w-6 h-6 object-contain"
            style="max-width: 100%; height: auto;"
            @error="handleImageError"
            @load="handleImageLoad"
          />
          <div v-else class="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
            <span class="text-gray-400 text-xs font-bold">{{ props.series.event?.charAt(0) }}</span>
          </div>
        </div>

        <!-- Scores and Teams -->
        <div class="flex flex-col gap-1 flex-1">
          <!-- Team 1 Row -->
          <div class="flex items-center gap-2">
            <div
              class="px-2 py-1 text-xs font-medium rounded"
              :class="props.series.team1.score > props.series.team2.score ? 'bg-success-900 text-success-300' : 'bg-danger-900 text-danger-300'"
            >
              {{ props.series.team1.score }}
            </div>
            <img
              v-if="props.series.team1.logo.night_url"
              :src="props.series.team1.logo.night_url"
              :alt="props.series.team1.name"
              class="w-4 h-4 rounded object-contain"
              style="max-width: 100%; height: auto;"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div
              v-else
              class="w-4 h-4 rounded bg-gray-700 flex items-center justify-center"
            >
              <span class="text-gray-400 text-xs">?</span>
            </div>
            <span class="text-text-primary text-xs">{{ props.series.team1.name }}</span>
          </div>

          <!-- Team 2 Row -->
          <div class="flex items-center gap-2">
            <div
              class="px-2 py-1 text-xs font-medium rounded"
              :class="props.series.team2.score > props.series.team1.score ? 'bg-success-900 text-success-300' : 'bg-danger-900 text-danger-300'"
            >
              {{ props.series.team2.score }}
            </div>
            <img
              v-if="props.series.team2.logo.night_url"
              :src="props.series.team2.logo.night_url"
              :alt="props.series.team2.name"
              class="w-4 h-4 rounded object-contain"
              style="max-width: 100%; height: auto;"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div
              v-else
              class="w-4 h-4 rounded bg-gray-700 flex items-center justify-center"
            >
              <span class="text-gray-400 text-xs">?</span>
            </div>
            <span class="text-text-primary text-xs">{{ props.series.team2.name }}</span>
          </div>
        </div>
      </div>

      <!-- Row 2: Event, Date, Format, and Action -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Left Column: Event and Date -->
        <div class="flex flex-col gap-1">
          <div class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.pro_series.event') }}</div>
          <div class="text-text-primary font-medium text-sm">{{ props.series.event }}</div>
          <div class="text-text-secondary text-xs">{{ formatDate(props.series.played_on) }}</div>
        </div>

        <!-- Right Column: Format and Action -->
        <div class="flex flex-col gap-1 items-end">
          <div class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.pro_series.format') }}</div>
          <div class="text-text-primary text-sm">{{ getBestOfFormat() }}</div>
          <router-link
            :to="{ name: 'ProSeriesPage', params: { id: props.series.uuid } }"
            class="text-primary-400 hover:text-primary-300 text-sm font-medium py-1 px-2 border border-gray-700 rounded-md hover:bg-gray-800"
          >
            {{ $t('coach.pro_series.view_series') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment';
import { getBestOfFormat as getBestOfFormatUtil } from '../../../../utils/array_utils';

const props = defineProps({
  series: {
    type: Object,
    required: true,
  },
});

function formatDate(dateString) {
  return moment(dateString).format('MMM Do, YYYY');
}

function getStatusBadge() {
  switch (props.series.status) {
    case 'completed':
      return 'Completed';
    case 'live':
      return 'Live';
    case 'upcoming':
      return 'Upcoming';
    default:
      return 'Unknown';
  }
}

function getBestOfFormat() {
  return getBestOfFormatUtil(props.series);
}

function handleImageError(event) {
  // Hide the broken image and show fallback
  event.target.style.display = 'none';
}

function handleImageLoad(event) {
  // Ensure the image is visible when it loads successfully
  event.target.style.display = 'block';
}
</script>
